/* Soles District - Shuffle Fit.

   Builds the "Shuffle fit" button on Build an Outfit, the per-slot lock and
   the opening outfit, and fills the stage with a fit that holds together.

   WHY THIS IS A SEPARATE FILE. It needs what is closed over inside
   assets/sd-fitting-room.js - the per-slot state, the colour helpers - so by
   rights it would live in there. That file is already at the size where the
   theme write tooling refuses it, so the module hands out a small handle on
   its root element (root.sdfr) and the shuffle picks it up here.

   The colour and set rules live in a third file, assets/sd-fit-colors.js,
   published as window.SDFitColors - see loadBrain() for why this file fetches
   it rather than the section loading it with a script tag, and for the one
   rule you must follow when editing it.

   ---------------------------------------------------------------------------
   THE PROMISE: EVERY PRESS PRODUCES A NEW OUTFIT

   Not necessarily new PIECES - the same jacket may come back in three fits
   running, and should, if it is the right jacket. What has to be new is the
   COMBINATION. A press that hands back an outfit you just saw reads as a
   broken button however good that outfit is.

   It is enforced on the whole fit, which is the part two earlier attempts got
   wrong by doing it per slot. Holding each slot's last few pieces out of its
   own draw sounds equivalent and is not: with three free slots and a tight
   colour, each slot had two or three live options, and the same few
   combinations came round again while every slot believed it had varied. So
   planFit() now proposes a whole outfit without committing it, the proposal
   is checked against the last several fits, and it is re-rolled until it is
   one you have not seen - bounded, so a slot with genuinely nothing left can
   never hang the button.

   ---------------------------------------------------------------------------
   WHAT MAKES A FIT READ AS DELIBERATE

   Four pieces picked at random are four statement colours fighting each other.
   What makes an outfit look considered is ONE statement colour and everything
   else neutral, tonal with it, or picking it up as an accent. So the shuffle
   decides the statement first and answers to it.

     1. THE STATEMENT. A colour on the wheel wins outright. Failing that, a
        pinned or locked piece - they arrived from it or kept it on purpose.
        Failing both, a slot is picked at random to carry it. It is taken from
        the anchor's WHOLE colour reading, so a bone tee with red lettering
        anchors a red fit rather than a beige one.

     2. THE SET. If the anchor is half of a set and the other half exists in a
        size they wear, that slot takes it. Not forced.

     3. THE BUDGET, if set. A hard ceiling on the whole outfit, applied before
        the colour ranking - a fit that quietly comes out $200 over the number
        they picked is a broken promise, not a trade-off.

     4. THE COLOUR. Neutral, same wedge, carrying the statement as an accent,
        or a neighbouring wedge. A second unrelated hue scores zero.

     5. THE TIEBREAKS, inside a band: price coherence, then feed position,
        which under the best-selling sort is the nearest thing to a popularity
        signal the storefront gives.

   WHY THERE IS A FLOOR ON THE POOL. Anchor a fit on a rare colour and the top
   band empties. On the GV Gallery Spring Sweatpants page, whose only vivid
   colour is orange, exactly one of 14 hats and one of 46 shoes carries orange
   - so the window selected that one piece every press, while 27 neutral shoes
   that would sit perfectly in an orange outfit were excluded for being a band
   lower. MIN_POOL is the floor: a slot always draws from at least that many
   of its best answers, reaching a band down when it has to. What it reaches
   for is the neutrals, which is what the outfit wanted anyway.

   Pieces with no colour recorded are allowed but ranked last - "cannot tell"
   should not outrank "known to go".

   The size is picked too, where My sizes says what they wear. */
(function () {
  var root = document.querySelector('[data-sdfr]');
  if (!root) return;

  /* ---- the brain --------------------------------------------------------
     assets/sd-fit-colors.js holds the colour and set rules. Fetched from here
     rather than loaded by a <script> tag in the section because
     sections/sd-fitting-room.liquid is 36KB - past what the theme write
     tooling takes in one go - so adding one line to it is not a one-line
     change. The URL is derived from this file's own script tag.

     >> THE RULE: the ?v= stamp is carried across from THIS file's URL, so the
     >> browser only picks up a new brain when THIS file also changes. If you
     >> edit sd-fit-colors.js, touch this file too - even a comment will do.
     >> Serving it unstamped instead would cache it forever and let it go
     >> silently stale, which is a far worse failure than an extra byte.

     If the brain cannot be reached nothing here is built. A shuffle with no
     colour rules is a randomiser, and a randomiser dressed up as "Shuffle
     fit" is worse than no button at all. */
  function loadBrain(done) {
    if (window.SDFitColors) { done(window.SDFitColors); return; }

    var me = document.querySelector('script[src*="sd-fit-shuffle"]');
    if (!me || !me.src) return;
    var at = me.src.indexOf('?');
    var stamp = at > -1 ? me.src.slice(at) : '';
    var base = (at > -1 ? me.src.slice(0, at) : me.src).replace('sd-fit-shuffle.js', 'sd-fit-colors.js');
    if (base.indexOf('sd-fit-colors.js') === -1) return;

    var tag = document.createElement('script');
    tag.src = base + stamp;
    tag.onload = function () { if (window.SDFitColors) done(window.SDFitColors); };
    document.head.appendChild(tag);
  }

  /* The two modules are separate <script> tags and the order they run in is
     not guaranteed, so wait for the handle rather than assuming it. */
  var waited = 0;
  (function ready() {
    if (root.sdfr) { loadBrain(function (C) { start(root.sdfr, C); }); return; }
    if ((waited += 50) > 8000) return;
    setTimeout(ready, 50);
  })();

  function start(sdfr, C) {
    var rows = sdfr.rows;
    var state = sdfr.state;

    function pickOne(list) { return list[Math.floor(Math.random() * list.length)]; }
    function isPinned(n) { return rows[n].hasAttribute('data-sdfr-pinned'); }
    function budget() {
      return typeof sdfr.budget === 'function' ? (sdfr.budget() || 0) : 0;
    }

    /* Slots in the outfit with something to offer. A dropped slot stays
       dropped: they took it out on purpose, and a shuffle that puts a hat back
       on someone who said no hat is not a shuffle, it is an argument. */
    function liveSlots() {
      var out = [];
      rows.forEach(function (row, n) {
        if (!state[n].off && state[n].items.length) out.push(n);
      });
      return out;
    }

    var RECALL = 4;        /* pieces each slot holds out of its own draw */
    var MIN_POOL = 8;      /* the floor on how many a slot chooses from */
    var FIT_RECALL = 12;   /* whole outfits remembered, to not repeat one */
    var FIT_TRIES = 10;    /* re-rolls before accepting a repeat */

    function recall(n, p) {
      var s = state[n];
      if (!s.recent) s.recent = [];
      if (!p) return;
      s.recent.push(p.id);
      while (s.recent.length > RECALL) s.recent.shift();
    }

    function seenLately(n, p) {
      var s = state[n];
      return !!(s.recent && p && s.recent.indexOf(p.id) > -1);
    }

    /* ---- the tiebreaks ---------------------------------------------------
       Deliberately small. They decide between pieces that already go with the
       outfit; they must never pull a clashing piece past a matching one, which
       is why the colour band is multiplied by 10 and these total at most 5. */

    /* A fit should sit in one price world. The band is wide on purpose - shoes
       genuinely cost more than a tee here, and a rule tight enough to stop a
       $400 jacket over $30 shorts would stop every real outfit in the shop. */
    function priceFit(p, anchor) {
      if (!anchor || !p || !p.price) return 1;
      var r = p.price / anchor;
      if (r >= 0.4 && r <= 2.5) return 2;
      if (r >= 0.2 && r <= 5) return 1;
      return 0;
    }

    function popFit(i) { return i < 8 ? 1 : 0; }

    /* ---- the budget ------------------------------------------------------
       What this slot may spend, given what the outfit has committed and what
       the slots after it cost at their cheapest. Without that last term the
       first slot spends the whole ceiling and the shoes come back empty. */
    function cheapest(n) {
      var items = state[n].items;
      var min = Infinity;
      for (var i = 0; i < items.length; i++) {
        if (items[i].price < min) min = items[i].price;
      }
      return min === Infinity ? 0 : min;
    }

    function allowanceFor(spent, rest) {
      var cap = budget();
      if (!cap) return 0;                   /* 0 means no ceiling at all */
      var reserved = 0;
      rest.forEach(function (n) { reserved += cheapest(n); });
      var left = cap - spent - reserved;
      return left > 0 ? left : -1;
    }

    /* Best available band, chosen at random within it. `cap` is the most this
       slot may spend, or 0 for no limit. */
    function bestIndex(n, seg, anchorPrice, cap) {
      var items = state[n].items;
      var scores = [];
      var candidates = [];

      for (var i = 0; i < items.length; i++) {
        var band = C.goesWith(items[i], seg);
        if (!band) { scores.push(-1); continue; }
        scores.push(band * 10 + priceFit(items[i], anchorPrice) * 2 + popFit(i));
        candidates.push(i);
      }
      if (!candidates.length) return null;

      /* The ceiling is a filter, not a score. If nothing matching is
         affordable, take the cheapest thing that matches rather than
         abandoning either the colour or the budget silently. */
      if (cap > 0) {
        var affordable = candidates.filter(function (i) { return items[i].price <= cap; });
        if (affordable.length) candidates = affordable;
        else {
          var low = candidates.slice().sort(function (a, b) { return items[a].price - items[b].price; });
          return low[0];
        }
      } else if (cap < 0) {
        var cheap = candidates.slice().sort(function (a, b) { return items[a].price - items[b].price; });
        return cheap[0];
      }

      var ranked = candidates.slice().sort(function (a, b) { return scores[b] - scores[a]; });
      var best = scores[ranked[0]];

      /* The window, then the floor that reaches a band down when the top band
         is nearly empty - see the header. */
      var pool = ranked.filter(function (i) { return scores[i] >= best - 3; });
      if (pool.length < MIN_POOL) pool = ranked.slice(0, Math.min(MIN_POOL, ranked.length));

      var fresh = pool.filter(function (i) { return !seenLately(n, items[i]); });
      if (fresh.length) pool = fresh;

      if (pool.length > 1) {
        var moved = pool.filter(function (i) { return i !== state[n].i; });
        if (moved.length) pool = moved;
      }
      return pickOne(pool);
    }

    /* The other half of a set, if this slot has it in a size they wear. */
    function setMateIndex(n, key, cap) {
      if (!key) return null;
      var want = sdfr.slotSizes(rows[n]);
      var items = state[n].items;
      var loose = null;
      for (var i = 0; i < items.length; i++) {
        if (C.setKey(items[i]) !== key) continue;
        if (cap > 0 && items[i].price > cap) continue;
        if (C.inSizes(items[i], want)) return i;
        if (loose === null) loose = i;
      }
      return want.length ? null : loose;
    }

    function autoSize(n) {
      var p = sdfr.current(n);
      state[n].vid = null;
      if (!p || sdfr.plain(p)) return;
      var want = sdfr.slotSizes(rows[n]);
      if (!want.length) return;
      for (var i = 0; i < p.variants.length; i++) {
        if (want.indexOf(p.variants[i].title) > -1) {
          state[n].vid = String(p.variants[i].id);
          return;
        }
      }
    }

    /* Land a slot on its new piece, arriving the way a swipe does rather than
       blinking into it - same classes the module's own step() uses. */
    function land(n, animate) {
      var media = rows[n].querySelector('[data-sdfr-media]');
      var s = state[n];
      if (s.anim) {
        clearTimeout(s.anim);
        s.anim = null;
        sdfr.SWIPE.forEach(function (c) { media.classList.remove(c); });
      }
      sdfr.draw(n);
      autoSize(n);
      recall(n, sdfr.current(n));
      if (!animate || sdfr.reducedMotion() || !media.querySelector('img')) return;
      media.classList.add('is-entering-right');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { media.classList.remove('is-entering-right'); });
      });
    }

    /* ---- planning a fit ---------------------------------------------------
       Proposes a whole outfit WITHOUT committing it, so the caller can throw
       it away and ask again if it is one we have just shown. Nothing in here
       may write to state[n].i - that is the entire point. */
    function planFit() {
      var live = liveSlots();
      if (!live.length) return null;

      var color = sdfr.color();
      var seg = null;
      if (color && !color.neutral && color.seg > -1) seg = color.seg;

      /* A pinned piece cannot move and a locked one must not, so they SET the
         statement rather than taking it. */
      var held = live.filter(function (n) { return isPinned(n) || state[n].locked; });
      var anchor = held.length ? sdfr.current(held[0]) : null;

      if (anchor && seg === null) {
        var opts = C.statementSegs(anchor);
        if (opts.length) seg = pickOne(opts);
      }

      var movable = live.filter(function (n) { return held.indexOf(n) === -1; });
      var picks = {};
      var spent = 0;
      held.forEach(function (n) {
        var p = sdfr.current(n);
        if (p) spent += p.price;
      });

      function remaining(self) {
        return movable.filter(function (m) { return m !== self && picks[m] === undefined; });
      }

      /* Nothing has claimed the statement, so a slot is sent to fetch it.
         Random order, which stops the statement piece always being the shoes. */
      if (seg === null) {
        var order = movable.slice().sort(function () { return Math.random() - 0.5; });
        for (var k = 0; k < order.length && seg === null; k++) {
          var n = order[k];
          var allow = allowanceFor(spent, remaining(n));
          var coloured = [];
          state[n].items.forEach(function (p, i) {
            if (!C.statementSegs(p).length) return;
            if (allow > 0 && p.price > allow) return;
            coloured.push(i);
          });
          if (!coloured.length) continue;
          var freshC = coloured.filter(function (i) { return !seenLately(n, state[n].items[i]); });
          if (freshC.length) coloured = freshC;
          var at = pickOne(coloured);
          picks[n] = at;
          anchor = state[n].items[at];
          spent += anchor.price;
          seg = pickOne(C.statementSegs(anchor));
        }
      }

      var anchorPrice = anchor ? anchor.price : null;

      var key = anchor ? C.setKey(anchor) : null;
      if (key) {
        movable.forEach(function (n) {
          if (picks[n] !== undefined) return;
          var mate = setMateIndex(n, key, allowanceFor(spent, remaining(n)));
          if (mate !== null) {
            picks[n] = mate;
            spent += state[n].items[mate].price;
          }
        });
      }

      movable.forEach(function (n) {
        if (picks[n] !== undefined) return;
        var at = bestIndex(n, seg, anchorPrice, allowanceFor(spent, remaining(n)));
        /* Every piece in the slot clashes - rather than leave it on whatever
           it was showing, which reads as the button missing that row, take
           anything and let the rest of the fit carry it. */
        if (at === null) at = Math.floor(Math.random() * state[n].items.length);
        picks[n] = at;
        if (state[n].items[at]) spent += state[n].items[at].price;
      });

      return { picks: picks, movable: movable, live: live };
    }

    /* What this outfit IS, for comparing one proposal with another. Held slots
       are included: the pinned product is part of the outfit even though it
       never moves, and leaving it out would be harmless here but wrong the
       moment a lock is toggled mid-session. */
    function fitSig(plan) {
      return plan.live.map(function (n) {
        var s = state[n];
        if (!s.items.length) return '0';
        var at = plan.picks[n] !== undefined ? plan.picks[n] : s.i;
        var p = s.items[at % s.items.length];
        return p ? String(p.id) : '0';
      }).join('-');
    }

    var recentFits = [];

    function shuffle(animate) {
      var plan = null;
      var sig = null;

      /* Re-roll until the outfit is one they have not just seen. Every part of
         planFit is randomised - which slot carries the statement, which hue it
         picks when a piece offers several, which of the top answers each slot
         lands on - so a retry genuinely explores rather than recomputing the
         same answer. Bounded: a catalogue with nothing left to offer must
         still return something rather than spin. */
      for (var t = 0; t < FIT_TRIES; t++) {
        var cand = planFit();
        if (!cand) return;
        plan = cand;
        sig = fitSig(cand);
        if (recentFits.indexOf(sig) === -1) break;
      }

      recentFits.push(sig);
      while (recentFits.length > FIT_RECALL) recentFits.shift();

      plan.movable.forEach(function (n) { state[n].i = plan.picks[n]; });
      plan.movable.forEach(function (n) { land(n, animate); });
      sdfr.paint();
    }

    /* ---- the opening outfit ----------------------------------------------
       The module starts every slot on a random piece, so the first thing
       anyone saw was four unrelated garments - the worst possible advert for a
       tool whose whole claim is that it puts outfits together. One silent
       shuffle once every slot has arrived fixes that, with no animation,
       because nothing has moved yet as far as the shopper is concerned. */
    var opened = false;
    var openWaited = 0;
    (function waitForSlots() {
      if (opened) return;
      var live = liveSlots();
      var ready = live.length === rows.filter(function (r, n) { return !state[n].off; }).length;
      if (live.length && ready) {
        opened = true;
        shuffle(false);
        return;
      }
      if ((openWaited += 120) > 10000) return;
      setTimeout(waitForSlots, 120);
    })();

    /* ---- the lock --------------------------------------------------------
       Built here, like the button, because the section is 36KB. A lock lives
       inside the media box so it sits on the garment it holds, and draw() puts
       it back when the module replaces that box's contents.

       A pinned row does not get one: the product page's own piece is already
       fixed, and a lock that cannot be unlocked is furniture, not a control. */
    function lockIcon(on) {
      return on
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3"/><rect x="5" y="10" width="14" height="10" rx="2.5"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 9.6-2"/><rect x="5" y="10" width="14" height="10" rx="2.5"/></svg>';
    }

    function paintLock(btn, n) {
      var on = !!state[n].locked;
      btn.innerHTML = lockIcon(on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', (on ? 'Unlock the ' : 'Lock the ') + rows[n].dataset.label.toLowerCase() +
        (on ? ' so the shuffle can change it' : ' so the shuffle leaves it alone'));
    }

    rows.forEach(function (row, n) {
      if (isPinned(n)) return;
      var media = row.querySelector('[data-sdfr-media]');
      if (!media) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sdfr__lock';
      paintLock(btn, n);

      /* The media box is also the swipe target and the arrows sit over it, so
         the click must stop here or locking a piece would also change it. */
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        state[n].locked = !state[n].locked;
        paintLock(btn, n);
        sdfr.draw(n);
        sdfr.paint();
      });

      media.appendChild(btn);
    });

    /* The lock's styles. In a stylesheet by rights, but the section that would
       load one is 36KB, and a <link> injected from here would need the same
       version-stamp dance as the brain for twenty lines. */
    var css = document.createElement('style');
    css.textContent =
      '[data-sdfr] .sdfr__media{position:relative}' +
      '[data-sdfr] .sdfr__lock{position:absolute;top:6px;right:6px;z-index:2;' +
        'width:30px;height:30px;display:flex;align-items:center;justify-content:center;' +
        'padding:0;border:1px solid rgba(16,20,24,.14);border-radius:50%;' +
        'background:rgba(255,255,255,.88);color:var(--sd-ink,#101418);cursor:pointer;' +
        'opacity:.45;transition:opacity .15s ease,background .15s ease,color .15s ease}' +
      '[data-sdfr] .sdfr__media:hover .sdfr__lock,[data-sdfr] .sdfr__lock:focus-visible{opacity:1}' +
      '[data-sdfr] .sdfr__lock svg{width:15px;height:15px;fill:none;stroke:currentColor;' +
        'stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}' +
      '[data-sdfr] .sdfr__lock[aria-pressed="true"]{opacity:1;background:var(--sd-accent,#00abd3);' +
        'border-color:var(--sd-accent,#00abd3);color:#fff}' +
      '[data-sdfr] .sdfr__row.is-locked .sdfr__arrow{opacity:.25;pointer-events:none}' +
      '[data-sdfr] [data-sdfr-total].is-over{color:#c8102e}';
    document.head.appendChild(css);

    /* ---- the button ------------------------------------------------------
       Built here rather than in the section markup: the section is 36KB, and a
       button that does nothing without JavaScript has no business being in the
       HTML anyway - with JS off this module has no garments to shuffle. */
    var controls = root.querySelector('.sdfr__controls');
    if (!controls) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sdfr__shuffle';
    btn.setAttribute('data-sdfr-shuffle', '');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
        '<rect x="3.7" y="3.7" width="16.6" height="16.6" rx="4" stroke="currentColor" stroke-width="1.9"/>' +
        '<circle cx="8.7" cy="8.7" r="1.45" fill="currentColor"/>' +
        '<circle cx="15.3" cy="8.7" r="1.45" fill="currentColor"/>' +
        '<circle cx="8.7" cy="15.3" r="1.45" fill="currentColor"/>' +
        '<circle cx="15.3" cy="15.3" r="1.45" fill="currentColor"/>' +
      '</svg><span>Shuffle fit</span>';

    btn.addEventListener('click', function () {
      btn.classList.remove('is-rolling');
      void btn.offsetWidth;
      btn.classList.add('is-rolling');
      shuffle(true);
    });
    btn.addEventListener('animationend', function () { btn.classList.remove('is-rolling'); });

    controls.appendChild(btn);
  }
})();

/* ---- Build a Fit order tracking ------------------------------------------
   Tags every line the "Add the fit to cart" button sends with hidden line-item
   properties, so an order shows in the admin which pieces came from the
   builder. Names start with an underscore, which Shopify keeps out of checkout
   and which the cart drawer and cart page skip, so the shopper never sees them.

     _source       "Build a Fit"
     _source_page  the page the builder was on
     _fit_id       one id per press, so pieces bought together as one outfit
                   can be grouped on the order

   Nothing reads this yet. Worth keeping anyway: a few weeks of it is the only
   honest answer to which pairings actually sell, and that is a better basis
   for the ranking above than anybody's colour theory.

   It works by watching for the add button's click (capture phase on the root,
   so it runs before the module's own handler) and adding the properties to the
   /cart/add.js request that click sends. Anything else posting to /cart/add.js
   is left alone, and if anything here fails the request goes out untagged
   rather than not at all. */
(function () {
  var root = document.querySelector('[data-sdfr]');
  if (!root || typeof window.fetch !== 'function') return;

  var armed = null;

  root.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-sdfr-add]') : null;
    if (!btn || btn.dataset.mode !== 'add') return;
    armed = String(Date.now()) + Math.floor(Math.random() * 1000);
    setTimeout(function () { armed = null; }, 0);
  }, true);

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (armed && url.indexOf('/cart/add') > -1 && init && typeof init.body === 'string') {
        var body = JSON.parse(init.body);
        if (body && Array.isArray(body.items)) {
          var fitId = armed;
          armed = null;
          body.items.forEach(function (item) {
            var props = item.properties || {};
            props._source = 'Build a Fit';
            props._source_page = window.location.pathname;
            props._fit_id = fitId;
            item.properties = props;
          });
          var copy = {};
          for (var k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) copy[k] = init[k]; }
          copy.body = JSON.stringify(body);
          init = copy;
        }
      }
    } catch (err) { /* send it untagged */ }
    return nativeFetch.call(window, input, init);
  };
})();
